import {
  Pagination as BasePagination,
  type PaginationRootProps,
} from "@ark-ui/react";
import { clsx } from "clsx";
import React from "react";

interface PaginationProps extends PaginationRootProps {
  classNames?: {
    root?: string;
    item?: string;
    trigger?: string;
    itemText?: string;
  };
  onPrevious?: React.MouseEventHandler<HTMLButtonElement>;
  onNext?: React.MouseEventHandler<HTMLButtonElement>;
}

const Pagination = React.forwardRef<
  React.ElementRef<typeof BasePagination.Root>,
  PaginationProps
>(({ classNames, className, onPrevious, onNext, ...props }, ref) => {
  const handlePreviousClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onPrevious?.(event);
    },
    [onPrevious],
  );

  const handleNextClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onNext?.(event);
    },
    [onNext],
  );

  return (
    <BasePagination.Root
      {...props}
      ref={ref}
      className={clsx(
        "ox-reset ox-PaginationRoot",
        classNames?.root,
        className,
      )}
    >
      <BasePagination.PrevTrigger
        className={clsx("ox-reset ox-PaginationTrigger", classNames?.trigger)}
        onClick={handlePreviousClick}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="currenColor"
          viewBox="0 0 256 256"
        >
          <path d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"></path>
        </svg>
      </BasePagination.PrevTrigger>
      <BasePagination.Context>
        {(pagination) =>
          pagination.pages.map((page, index) =>
            page.type === "page" ? (
              <BasePagination.Item
                key={index}
                {...page}
                className={clsx("ox-reset ox-PaginationItem", classNames?.item)}
              >
                <span className={classNames?.itemText}>{page.value}</span>
              </BasePagination.Item>
            ) : (
              <BasePagination.Ellipsis
                key={index}
                index={index}
                className={classNames?.item}
              >
                &#8230;
              </BasePagination.Ellipsis>
            ),
          )
        }
      </BasePagination.Context>
      <BasePagination.NextTrigger
        className={clsx("ox-reset ox-PaginationTrigger", classNames?.trigger)}
        onClick={handleNextClick}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="currenColor"
          viewBox="0 0 256 256"
        >
          <path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"></path>
        </svg>
      </BasePagination.NextTrigger>
    </BasePagination.Root>
  );
});

Pagination.displayName = "Pagination";
export { Pagination };
export type { PaginationProps };
